import React, { useEffect, useState } from 'react';
import Chart from 'react-google-charts';
import { scroller } from 'react-scroll';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import {
    getEventComments,
    searchAppEvents,
    getActivityFilters,
} from '../../../actions/account-activity/accountActivityActionCreators';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import AccountActivityFilters from './AccountActivityFilters';
import { AccountActivityEvents } from './AccountActivityEvents';
import { StyledNoRecordsFound } from './AccountActivity.styled';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ACCOUNT_ACTIVITY_CHART_GRIDLINES_COUNT } from './common/accountActivity.constants';
import { MMM_DD_YYYY_HH_MM, getEndOfDay, getStartOfDay } from '../../../utils/dates/dateMethods';
import { SideNavContainer } from '../utils';
import { IAccountActivityEvent } from './common/types';
import { PAGE_TYPE } from '../utils';
import MemberDetailsContainer from '../common/MemberDetailsContainer';

const OPTIONS_ALL = { value: 'ALL', text: 'All' };

const AccountActivityContainer = () => {
    const dispatch = useAppDispatch();
    const { activityData, initialData, filters, currentFilteredData } = useAppSelector(
        ({ accountActivity }) => accountActivity,
    );
    const DATE_RANGE_FILTER_SIZE = 2;
    const [categoryOptions, setCategoryOptions] = useState([OPTIONS_ALL]);
    const [eventTypeOptions, setEventTypeOptions] = useState([OPTIONS_ALL]);

    const [chartData, setChartData] = useState<any[]>([]);
    const [activeKey, setActiveKey] = useState<string | null>(null);

    const { id: encHouseholdId } = useParams();

    useEffect(() => {
        if (!currentFilteredData) return;

        if (filters.category !== OPTIONS_ALL.value) {
            const existingEventTypeOptions = new Set<string>();
            activityData
                .filter((activity: IAccountActivityEvent) => activity.type === filters.category)
                .forEach((item: IAccountActivityEvent) => existingEventTypeOptions.add(item.name));
            setEventTypeOptions([
                OPTIONS_ALL,
                ...Array.from(existingEventTypeOptions).map(type => ({ value: type, text: type })),
            ]);
        } else {
            setEventTypeOptions([OPTIONS_ALL]);
        }
        updateChartData(currentFilteredData);
    }, [currentFilteredData]);

    useEffect(() => {
        if (!activityData) return;
        const types = new Set<string>();
        activityData.forEach(item => types.add(item.type));
        const categoryOptionList = Array.from(types)
            .map(type => ({ value: type, text: type }))
            .sort((a, b) => a.text.localeCompare(b.text));
        setCategoryOptions([OPTIONS_ALL, ...categoryOptionList]);
    }, [activityData]);

    useEffect(() => {
        dispatch(getActivityFilters({ encHouseholdId }));
        fetchAppEvents();
    }, []);

    useEffect(() => {
        setActiveKey(null);
    }, [filters]);

    function applyFilters(data: IAccountActivityEvent[], currentFilters = filters) {
        // Helper function to check if a value meets the filter criteria
        const meetsCriteria = (itemValue: any, filterValue: any) =>
            filterValue === OPTIONS_ALL.value || itemValue.toLowerCase() === filterValue.toLowerCase();

        // Helper function to check date range
        const inDateRange = (timestamp: string, [startDate, endDate]: [string, string]) => {
            const date = new Date(timestamp);
            return getStartOfDay(new Date(startDate)) <= date && date <= getEndOfDay(new Date(endDate));
        };

        return data.filter(item => {
            const { category, eventType, createdByRole, dateRange } = currentFilters;

            // Check non-date filters
            if (
                !meetsCriteria(item.type, category) ||
                !meetsCriteria(item.name, eventType) ||
                !meetsCriteria(item.createdByUserRole, createdByRole)
            ) {
                return false;
            }

            // Check date range filter
            if (
                dateRange?.length === DATE_RANGE_FILTER_SIZE &&
                dateRange[0] &&
                dateRange[1] &&
                !inDateRange(item.creationTimeStamp, dateRange)
            ) {
                return false;
            }

            return true;
        });
    }

    const chartOptions = {
        title: '',
        hAxis: {
            title: '',
            type: 'datetime',
            format: MMM_DD_YYYY_HH_MM,
            gridlines: {
                count: ACCOUNT_ACTIVITY_CHART_GRIDLINES_COUNT,
            },
            viewWindow: {},
        },
        tooltip: { isHtml: true },
        vAxis: {
            textPosition: 'none',
            ticks: [1],
            gridlines: { count: 1 },
        },
        legend: 'none',
        height: 150,
    };

    function updateChartData(data: IAccountActivityEvent[]) {
        const chartData: [any] = [['Date', 'Value', { role: 'tooltip', type: 'string', p: { html: true } }]];
        const creationTimeList = data.map(item => new Date(item.creationTimeStamp));
        for (let index = 0; index < data.length; index++) {
            const activity = data[index];
            const date = new Date(activity.creationTimeStamp);
            const tooltip = `<div style="padding:10px;">
                                <strong>${activity.name}</strong><br/>
                                <span>${date}</span>
                             </div>`;
            chartData.push([date, 1, tooltip]);
        }
        chartOptions.hAxis.viewWindow['min'] = new Date(
            Math.min(...creationTimeList.map((date: Date) => date.getTime())),
        );
        chartOptions.hAxis.viewWindow['max'] = new Date(
            Math.max(...creationTimeList.map((date: Date) => date.getTime())),
        );
        setChartData(chartData);
    }

    function dispatchFormAction(action: Function, data = {}, extraArgs = {}) {
        const formData = new FormData();
        formData.append('csrftoken', window.csrf_token);

        // Add all key-value pairs from `data` to the form data
        Object.entries(data).forEach(([key, value]) => formData.append(key, String(value)));

        // Dispatch the action with payload and any additional arguments
        dispatch(action({ payload: formData, ...extraArgs }));
    }

    function fetchAppEvents() {
        dispatchFormAction(searchAppEvents, { moduleId: encHouseholdId }, { applyFilters });
    }

    const fetchComments = (eventId: string) => {
        dispatchFormAction(getEventComments, { encAppEventId: eventId }, { eventId });
    };

    const handleScroll = (id: string) => {
        scroller.scrollTo(id, {
            duration: 800,
            delay: 0,
            offset: -100,
            smooth: 'easeInOutQuart',
            containerId: 'containerElement',
        });
    };

    const isDataPresent = currentFilteredData.length > 0;

    if (!initialData) {
        return null;
    }

    return (
        <SideNavContainer page={PAGE_TYPE.ACCOUNT_ACTIVITY}>
            <MemberDetailsContainer id="account-activity" heading="member.account.activity.title">
                <AccountActivityFilters
                    categoryOptions={categoryOptions}
                    eventTypeOptions={eventTypeOptions}
                    refetchData={fetchAppEvents}
                    applyFilters={applyFilters}
                />
                <ConditionalRendering isTrue={!isDataPresent}>
                    <StyledNoRecordsFound data-testid="no-record-found-label">
                        <span>
                            <FormattedMessage id="member.account.activity.no.records.found" />
                        </span>
                    </StyledNoRecordsFound>
                </ConditionalRendering>
                <ConditionalRendering isTrue={isDataPresent}>
                    <div data-testid="account-activity-chart">
                        <Chart
                            chartType="ScatterChart"
                            width="100%"
                            height="100%"
                            data={chartData}
                            options={chartOptions}
                            chartEvents={[
                                {
                                    eventName: 'ready',
                                    callback: ({ chartWrapper, google }) => {
                                        const chart = chartWrapper.getChart();
                                        const table = chartWrapper.getDataTable();
                                        if (!table) return;
                                        currentFilteredData.map((item: any, i: number) =>
                                            table.setRowProperty(i, 'Id', item.id),
                                        );
                                        (google.visualization.events as any).addListener(
                                            chart,
                                            'onmouseover',
                                            (e: { row: number }) => {
                                                const { row } = e;
                                                const table = chartWrapper.getDataTable();
                                                const id = table?.getRowProperty(row, 'Id');
                                                setActiveKey(id);
                                                setTimeout(() => {
                                                    handleScroll(id);
                                                }, 0);
                                            },
                                        );
                                    },
                                },
                            ]}
                        />
                    </div>
                    <AccountActivityEvents
                        activeKey={activeKey}
                        setActiveKey={setActiveKey}
                        fetchComments={fetchComments}
                    />
                </ConditionalRendering>
            </MemberDetailsContainer>
        </SideNavContainer>
    );
};

export default AccountActivityContainer;
